import type { Entry, Lift } from "../../types/dataTypes";
import type {
  WrpfCurrentRecord,
  WrpfEntryAnchor,
  WrpfRecordCategory,
  WrpfRecordJurisdiction,
} from "../handoff/types";
import { wrpfCompetitionWeightClass } from "../handoff/modalityMapping";

export type RecordSuggestion = {
  jurisdiction: WrpfRecordJurisdiction;
  discipline: WrpfCurrentRecord["discipline"];
  division: string;
  weightClass: string;
  liftType: WrpfCurrentRecord["liftType"];
  candidateKg: number;
  currentKg: number;
  incumbentName: string | null;
  incumbentBodyweightKg: number | null;
  tieBreak: "WEIGHT" | "STANDARD";
};

type EvaluationInput = {
  entry: Entry;
  anchor: WrpfEntryAnchor | undefined;
  lift: Lift;
  attemptOneIndexed: number;
  recordCategories: ReadonlyArray<WrpfRecordCategory>;
  currentRecords: ReadonlyArray<WrpfCurrentRecord>;
  localEntries: ReadonlyArray<Entry>;
  localAnchors: ReadonlyArray<WrpfEntryAnchor>;
};

const JURISDICTION_ORDER: ReadonlyArray<WrpfRecordJurisdiction> = [
  "NOA",
  "NEA",
  "CUYO",
  "PAMPEANA",
  "PATAGONICA",
  "NATIONAL",
  "IBEROAMERICAN",
  "WORLD",
  "INTERNATIONAL",
];

type RegionalJurisdiction = "NOA" | "NEA" | "CUYO" | "PAMPEANA" | "PATAGONICA";

const PROVINCE_REGIONS: Record<string, RegionalJurisdiction> = {
  CATAMARCA: "NOA",
  JUJUY: "NOA",
  SALTA: "NOA",
  SANTIAGO_DEL_ESTERO: "NOA",
  TUCUMAN: "NOA",
  CHACO: "NEA",
  CORRIENTES: "NEA",
  FORMOSA: "NEA",
  MISIONES: "NEA",
  ENTRE_RIOS: "NEA",
  MENDOZA: "CUYO",
  SAN_JUAN: "CUYO",
  SAN_LUIS: "CUYO",
  LA_RIOJA: "CUYO",
  BUENOS_AIRES: "PAMPEANA",
  CABA: "PAMPEANA",
  CORDOBA: "PAMPEANA",
  LA_PAMPA: "PAMPEANA",
  SANTA_FE: "PAMPEANA",
  CHUBUT: "PATAGONICA",
  NEUQUEN: "PATAGONICA",
  RIO_NEGRO: "PATAGONICA",
  SANTA_CRUZ: "PATAGONICA",
  TIERRA_DEL_FUEGO: "PATAGONICA",
};

const normalizeToken = (value: string): string =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_|_$/g, "");

const isArgentine = (country: string): boolean => {
  const normalized = normalizeToken(country);
  return normalized === "AR" || normalized === "ARG" || normalized === "ARGENTINA";
};

const IBERO_AMERICAN_COUNTRIES = new Set([
  "AD",
  "ANDORRA",
  "AR",
  "ARG",
  "ARGENTINA",
  "BO",
  "BOLIVIA",
  "BR",
  "BRASIL",
  "BRAZIL",
  "CL",
  "CHILE",
  "CO",
  "COLOMBIA",
  "CR",
  "COSTA_RICA",
  "CU",
  "CUBA",
  "DO",
  "DOMINICAN_REPUBLIC",
  "REPUBLICA_DOMINICANA",
  "EC",
  "ECUADOR",
  "ES",
  "ESPANA",
  "SPAIN",
  "GT",
  "GUATEMALA",
  "HN",
  "HONDURAS",
  "MX",
  "MEXICO",
  "NI",
  "NICARAGUA",
  "PA",
  "PANAMA",
  "PE",
  "PERU",
  "PT",
  "PORTUGAL",
  "PY",
  "PARAGUAY",
  "SV",
  "EL_SALVADOR",
  "UY",
  "URUGUAY",
  "VE",
  "VENEZUELA",
]);

const isIberoAmerican = (country: string): boolean =>
  IBERO_AMERICAN_COUNTRIES.has(normalizeToken(country));

const eligibleJurisdictions = (
  entry: Entry,
  categories: ReadonlyArray<WrpfRecordCategory>,
): WrpfRecordJurisdiction[] => {
  const result: WrpfRecordJurisdiction[] = [];
  const enabled = new Set(categories);
  if (isArgentine(entry.country)) {
    const region = PROVINCE_REGIONS[normalizeToken(entry.state)];
    if (enabled.has("REGIONAL") && region) result.push(region);
    if (enabled.has("NATIONAL")) result.push("NATIONAL");
  }
  if (enabled.has("IBEROAMERICAN") && isIberoAmerican(entry.country)) {
    result.push("IBEROAMERICAN");
  }
  if (enabled.has("WORLD")) result.push("WORLD");
  return [...new Set(result)].sort(
    (a, b) => JURISDICTION_ORDER.indexOf(a) - JURISDICTION_ORDER.indexOf(b),
  );
};

const equipment = (entry: Entry): WrpfCurrentRecord["equipment"] => {
  const value = entry.equipment.toLowerCase();
  if (value.includes("multi") || value.includes("unlimited")) return "MULTI_PLY";
  if (value.includes("single")) return "SINGLE_PLY";
  if (value.includes("wrap")) return "CLASSIC";
  return "RAW";
};

const disciplineFromEvent = (event: string): WrpfCurrentRecord["discipline"] | null => {
  const value = event.split("_")[0];
  return value === "SBD" || value === "BD" || value === "S" || value === "B" || value === "D" ? value : null;
};

const equipmentFromEvent = (event: string): WrpfCurrentRecord["equipment"] => {
  if (event.endsWith("_MULTI_PLY")) return "MULTI_PLY";
  if (event.endsWith("_SINGLE_PLY")) return "SINGLE_PLY";
  if (event.endsWith("_CLASSIC")) return "CLASSIC";
  return "RAW";
};

type Classification = {
  discipline: WrpfCurrentRecord["discipline"];
  division: string;
};

const classificationsFor = (anchor: WrpfEntryAnchor, entry: Entry): Classification[] => {
  const result: Classification[] = [];
  const modalities = [
    { event: anchor.primaryEvent, division: anchor.primaryDivision },
    ...(anchor.secondaryModalities ?? []),
  ];
  for (const modality of modalities) {
    const discipline = disciplineFromEvent(modality.event);
    if (
      discipline === null ||
      equipmentFromEvent(modality.event) !== equipment(entry) ||
      (entry.events.length > 0 && !entry.events.includes(discipline)) ||
      result.some(
        (candidate) =>
          candidate.discipline === discipline && candidate.division === modality.division,
      )
    ) {
      continue;
    }
    result.push({ discipline, division: modality.division });
  }
  return result;
};

const liftTypeFor = (lift: Lift): WrpfCurrentRecord["liftType"] =>
  lift === "S" ? "SQUAT" : lift === "B" ? "BENCH_PRESS" : "DEADLIFT";

const bestSuccessful = (weights: ReadonlyArray<number>, statuses: ReadonlyArray<number>, limit = 4): number => {
  let best = 0;
  for (let index = 0; index < Math.min(limit, weights.length); index++) {
    if ((statuses[index] ?? 0) > 0) best = Math.max(best, weights[index] ?? 0);
  }
  return best;
};

const projectedMark = (entry: Entry, lift: Lift, attemptOneIndexed: number): number => {
  const weights = lift === "S" ? entry.squatKg : lift === "B" ? entry.benchKg : entry.deadliftKg;
  return weights[attemptOneIndexed - 1] ?? 0;
};

const projectedTotal = (entry: Entry, attemptOneIndexed: number): number => {
  if (attemptOneIndexed > 3) return 0;
  const candidateDeadlift = projectedMark(entry, "D", attemptOneIndexed);
  if (candidateDeadlift <= 0) return 0;
  const squat = bestSuccessful(entry.squatKg, entry.squatStatus, 3);
  const bench = bestSuccessful(entry.benchKg, entry.benchStatus, 3);
  const previousDeadlift = bestSuccessful(entry.deadliftKg, entry.deadliftStatus, 3);
  return squat + bench + Math.max(previousDeadlift, candidateDeadlift);
};

const classificationMatches = (
  record: WrpfCurrentRecord,
  classification: Classification,
  entry: Entry,
  recordLiftType: WrpfCurrentRecord["liftType"],
): boolean =>
  record.division === classification.division &&
  record.discipline === classification.discipline &&
  record.equipment === equipment(entry) &&
  record.liftType === recordLiftType;

const localMarkKg = (
  entry: Entry,
  liftType: WrpfCurrentRecord["liftType"],
  eventDiscipline: WrpfCurrentRecord["discipline"],
): number => {
  if (liftType === "SQUAT") return bestSuccessful(entry.squatKg, entry.squatStatus);
  if (liftType === "BENCH_PRESS") return bestSuccessful(entry.benchKg, entry.benchStatus);
  if (liftType === "DEADLIFT") return bestSuccessful(entry.deadliftKg, entry.deadliftStatus);
  const squat = eventDiscipline === "SBD" ? bestSuccessful(entry.squatKg, entry.squatStatus, 3) : 0;
  const bench = bestSuccessful(entry.benchKg, entry.benchStatus, 3);
  const deadlift = bestSuccessful(entry.deadliftKg, entry.deadliftStatus, 3);
  if ((eventDiscipline === "SBD" && squat === 0) || bench === 0 || deadlift === 0) return 0;
  return squat + bench + deadlift;
};

export function evaluateRecordAttempt(input: EvaluationInput): RecordSuggestion[] {
  const { anchor, entry } = input;
  if (entry.guest || !anchor || input.attemptOneIndexed < 1 || input.attemptOneIndexed > 4) return [];
  const candidateLiftKg = projectedMark(entry, input.lift, input.attemptOneIndexed);
  if (candidateLiftKg <= 0) return [];

  const jurisdictions = eligibleJurisdictions(entry, input.recordCategories);
  // La categoría del pesaje, no la inscripta: si pesó fuera de su categoría compite (y se publica) en
  // la nueva, así que los récords a batir son los de esa.
  const weightClass = wrpfCompetitionWeightClass(entry, anchor) ?? anchor.weightClass;
  const suggestions: RecordSuggestion[] = [];
  for (const classification of classificationsFor(anchor, entry)) {
    if (!classification.discipline.includes(input.lift)) continue;
    const marks: Array<{ liftType: WrpfCurrentRecord["liftType"]; kg: number }> = [
      { liftType: liftTypeFor(input.lift), kg: candidateLiftKg },
    ];
    if (
      input.lift === "D" &&
      (classification.discipline === "SBD" || classification.discipline === "BD")
    ) {
      const total = projectedTotal(entry, input.attemptOneIndexed);
      if (total > 0) marks.push({ liftType: "TOTAL", kg: total });
    }

    for (const mark of marks) {
      for (const jurisdiction of jurisdictions) {
        const incumbent = input.currentRecords.find(
          (record) =>
            record.jurisdiction === jurisdiction &&
            record.sex === anchor.sex &&
            record.weightClass === weightClass &&
            classificationMatches(record, classification, entry, mark.liftType),
        );
        if (!incumbent) continue;

        let hasActiveMark = incumbent.sourceResultId !== null || incumbent.athleteName !== null;
        let activeKg = incumbent.weightKg;
        let activeName = incumbent.athleteName;
        let activeBodyweightKg = incumbent.bodyweightKg;
        for (const localEntry of input.localEntries) {
          const localAnchor = input.localAnchors.find(
            (candidate) => candidate.openlifterEntryId === localEntry.id,
          );
          if (
            !localAnchor ||
            localAnchor.sex !== anchor.sex ||
            wrpfCompetitionWeightClass(localEntry, localAnchor) !== weightClass ||
            equipment(localEntry) !== equipment(entry) ||
            !classificationsFor(localAnchor, localEntry).some(
              (candidate) =>
                candidate.discipline === classification.discipline &&
                candidate.division === classification.division,
            ) ||
            !eligibleJurisdictions(localEntry, input.recordCategories).includes(jurisdiction)
          ) {
            continue;
          }
          const localKg = localMarkKg(localEntry, mark.liftType, classification.discipline);
          const localWins = localKg > activeKg || (localKg === activeKg && !hasActiveMark);
          if (localWins) {
            hasActiveMark = true;
            activeKg = localKg;
            activeName = localEntry.name;
            activeBodyweightKg = localEntry.bodyweightKg > 0 ? localEntry.bodyweightKg : null;
          }
        }

        const currentKg = activeKg;
        const winsByWeight = mark.kg > currentKg;
        const claimsVacantStandard = mark.kg === currentKg && !hasActiveMark;
        if (!winsByWeight && !claimsVacantStandard) continue;
        suggestions.push({
          jurisdiction,
          discipline: classification.discipline,
          division: classification.division,
          weightClass,
          liftType: mark.liftType,
          candidateKg: mark.kg,
          currentKg,
          incumbentName: activeName,
          incumbentBodyweightKg: activeBodyweightKg,
          tieBreak: hasActiveMark ? "WEIGHT" : "STANDARD",
        });
      }
    }
  }
  return suggestions.sort(
    (a, b) =>
      JURISDICTION_ORDER.indexOf(a.jurisdiction) - JURISDICTION_ORDER.indexOf(b.jurisdiction) ||
      a.discipline.localeCompare(b.discipline) ||
      a.division.localeCompare(b.division) ||
      a.liftType.localeCompare(b.liftType),
  );
}
